import React from 'react';

import {zodResolver} from '@hookform/resolvers/zod';
import {Colors, CtaButton, Title} from '@optimaxdev/design-system';
import classNames from 'classnames';
import {FormProvider, useForm} from 'react-hook-form';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {ApplicationStoreType} from 'desktop/store';
import {AddressFormType, CurrentAddressType} from 'desktop/types/addressType';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';

import {STEPS} from '../../constants/checkout';
import {defaultAddress} from '../../constants/deliveryStep';
import {
    startAddNewCustomerAddressSaga,
    startSavingCustomerAddressSaga,
} from '../../store/reducers/deliveryStep/customerAddresses/customerAddresses';
import {getEditStepError, getEditStepLoading} from '../../store/selectors/editStep/editStep';

import {useDeliveryFormValidationSchema} from '../../utils/deliveryFormValidation/deliveryFormValidation';
import {DeliveryFormFields} from '../deliveryFormFields/deliveryFormFields';
import {usePhoneSubscriptionRequest} from '../newsletterSubscription/usePhoneSubscriptionRequest';
import {StepTitle} from '../stepTitle/stepTitle';
import {StepWrapper} from '../stepWrapper/stepWrapper';

import {EditFormFromReduxUpdaterConnected} from './editFormFromReduxUpdater';

import s from './deliveryStepEditAddress.scss';

export type PropsType = {
    isLoading: boolean;
    error: string;
    addressType: CurrentAddressType;
    pageType: 'new' | 'edit';
    saveNewAddress: (addressType: CurrentAddressType) => void;
    updateAddress: (addressType: CurrentAddressType) => void;
};

/**
 * Delivery step edit address for checkout
 *
 * @constructor
 */
export const DeliveryStepEditAddress: React.FC<PropsType> = ({
    addressType,
    error,
    isLoading,
    pageType,
    saveNewAddress,
    updateAddress,
}) => {
    const navigate = useNavigate();
    const schema = useDeliveryFormValidationSchema({onlyForType: addressType});
    const methods = useForm<AddressFormType>({
        defaultValues: {
            billingAddress: defaultAddress,
            shippingAddress: defaultAddress,
        },
        mode: 'onTouched',
        resolver: zodResolver(schema),
    });
    const {isOneStepAvailable} = useIsOneStepAvailable();

    const saveEditedAddress = () => {
        if (pageType === 'new') {
            saveNewAddress(addressType);
        } else {
            updateAddress(addressType);
        }
    };

    usePhoneSubscriptionRequest();

    return (
        <StepWrapper step={STEPS.DELIVERY_STEP}>
            {isOneStepAvailable ? (
                <div className={s.oneStepTitle}>
                    <Title color={Colors.blueCharcoal} level={2} lineHeight="40px">
                        {pageType === 'new' ? 'New Address' : 'Edit Address'}
                    </Title>
                </div>
            ) : (
                <StepTitle currentStep={STEPS.DELIVERY_STEP} step={STEPS.DELIVERY_STEP}>
                    Delivery
                </StepTitle>
            )}

            <FormProvider {...methods}>
                <form
                    className={classNames(s.wrapper, {[s.oneStepWrapper]: isOneStepAvailable})}
                    name="CheckoutEditAddressForm"
                    onSubmit={methods.handleSubmit(saveEditedAddress)}
                >
                    <fieldset disabled={isLoading}>
                        <DeliveryFormFields target={`${addressType}Address`} />
                        {error && <div className={s.error}>{error}</div>}
                        <div className={s.buttons}>
                            <div className={s.button}>
                                <CtaButton
                                    onClick={() => navigate('/checkout/onepage')}
                                    size="large"
                                    variant="ghost"
                                >
                                    Cancel
                                </CtaButton>
                            </div>
                            <div className={s.button}>
                                <CtaButton isLoading={isLoading} size="large" type="submit">
                                    Save
                                </CtaButton>
                            </div>
                        </div>
                    </fieldset>
                </form>

                <EditFormFromReduxUpdaterConnected addressType={addressType} pageType={pageType} />
            </FormProvider>
        </StepWrapper>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    error: getEditStepError(store),
    isLoading: getEditStepLoading(store),
});

const mapDispatchToProps = {
    saveNewAddress: startAddNewCustomerAddressSaga,
    updateAddress: startSavingCustomerAddressSaga,
};

export const DeliveryStepEditAddressConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(DeliveryStepEditAddress);
